import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import { t } from 'ember-intl'
import { eq, or } from 'ember-truth-helpers'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import AssignmentStatus from 'teacher-ui-client/components/assignments-admin/assignment-status'
import type Assignment from 'teacher-ui-client/models/assignment'
import { service } from '@ember/service'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type Store from '@ember-data/store'
import type ReadAloudReview from 'teacher-ui-client/models/read-aloud-review'
import { colspanMax } from 'teacher-ui-client/modifiers/colspan-max'
import UiButton from 'teacher-ui-client/components/ui-button'
import type Student from 'teacher-ui-client/models/student'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { ReadAloudReviewModal } from 'teacher-ui-client/components/assignments-admin/read-aloud-review-modal'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import type Owner from '@ember/owner'

interface AssignedStudentsViewModel {
  studentId: string
  status: string
  schoolClassNames: string
  student?: Student
  studentReviewForAssignment?: ReadAloudReview
}

interface Signature {
  Element: HTMLDivElement
  Args: {
    assignment: Assignment
  }
}

/**
 * Table of students for a particular assignment
 */
export class InnerStudentReviewsTable extends Component<Signature> {
  @service store!: Store

  @service subscriptionType!: SubscriptionTypeService

  @tracked currentSortColumn?: string

  @tracked sortingPriorityQueue = ['firstName', 'lastName', 'gradePosition'].map((columnProp) => ({
    key: columnProp,
    sortDirection: 'asc' as const,
  }))

  processor: DataProcessor<AssignedStudentsViewModel>

  get data() {
    return this.assignedStudents
  }

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get assignedStudents(): AssignedStudentsViewModel[] {
    const { assignment } = this.args
    if (!assignment?.assignedStudents) return []

    const students = this.store.peekAll<Student>('student')
    const subscriptionType = this.subscriptionType.current

    // Match assignment students to their data & reviews
    return assignment.assignedStudents.map((assignedStudent) => {
      const student = students.find((s) => s.id === String(assignedStudent.canonical_student_id))
      const schoolClassNames =
        student?.schoolClasses
          ?.filter((sc) => sc.subscriptionType === subscriptionType)
          .map((sc) => sc.name)
          .join(', ') ?? ''

      const { canonical_student_id, status } = assignedStudent

      return {
        studentId: String(canonical_student_id),
        status,
        student,
        schoolClassNames,
        // ensures table updates when new review is created, without needing to rerun the reviews query
        studentReviewForAssignment: this.store
          .peekAll<ReadAloudReview>('read-aloud-review')
          .find(
            (review) =>
              review.assignmentUuid === assignment.id &&
              review.student?.id === String(assignedStudent.canonical_student_id),
          ),
      }
    })
  }

  @tracked editReviewRecord?: ReadAloudReview

  editReview = (review: ReadAloudReview) => {
    this.editReviewRecord = review
  }

  createReview = (forStudent: Student) => {
    if (!this.args.assignment.id) return
    this.editReviewRecord = this.store.createRecord<ReadAloudReview>('read-aloud-review', {
      assignmentUuid: this.args.assignment.id,
      student: forStudent,
    })
  }

  closeReview = () => {
    this.editReviewRecord = undefined
  }

  <template>
    <div class="w-full overflow-auto" data-test-inner-student-reviews-table ...attributes>
      {{#let this.processor.transformedData this.processor.tableAction as |transformedData|}}
        <table class="w-full table-fixed divide-y divide-white">
          <thead>
            <tr class="divide-x divide-white">
              <th
                class="bg-juicy-orange-100 min-w-fit p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "student.firstName")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.firstName"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="student.firstName"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 min-w-fit p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "student.lastName")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.lastName"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="student.lastName"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "schoolClassNames")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.classes"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="schoolClassNames"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 w-24 p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "student.gradePosition")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "grade.label"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="student.gradePosition"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 w-40 p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "status")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.studentStatus"}}
                  </span>
                  <SortColumnIcon @sortingPriorityQueue={{this.processor.sortingPriorityQueue}} @columnName="status" />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 w-36 p-3"
                {{on "click" (fn this.processor.tableActions.sortByNumber "studentReviewForAssignment.score")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.score"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="studentReviewForAssignment.score"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 w-36 p-3"
                {{on "click" (fn this.processor.tableActions.sortByNumber "studentReviewForAssignment.wordsPerMinute")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.wordsPerMinute"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="studentReviewForAssignment.worsePerMinute"
                  />
                </div>
              </th>
              <th class="bg-juicy-orange-100 p-3">
                {{t "assignments.headings.comments"}}
              </th>
              <th class="bg-juicy-orange-100 p-3">
                {{t "assignments.headings.reviewAudio"}}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-white">
            {{#each transformedData as |row|}}
              <tr data-test-inner-student-reviews-table-row={{row.studentId}}>
                {{#if row.student}}
                  <td data-test-first-name class="bg-juicy-orange-50 p-3">
                    {{row.student.firstName}}
                  </td>
                  <td class="bg-juicy-orange-50 p-3 text-left">
                    {{row.student.lastName}}
                  </td>
                {{else}}
                  <td class="bg-juicy-orange-50 p-3 text-left">
                    {{t "assignments.assignedStudentNotFound"}}
                  </td>
                  <td class="bg-juicy-orange-50 p-3 text-left"></td>
                {{/if}}
                <td class="bg-juicy-orange-50 p-3 text-left">
                  {{row.schoolClassNames}}
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left">
                  {{formatValue (gradeName row.student.gradePosition)}}
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left">
                  <AssignmentStatus @status={{row.status}} class="float-left" />
                </td>
                <td data-test-review-score class="bg-juicy-orange-50 p-3 text-left">
                  {{formatValue row.studentReviewForAssignment.score append=" / 5"}}
                </td>
                <td data-test-review-wpm class="bg-juicy-orange-50 p-3 text-left">
                  {{formatValue row.studentReviewForAssignment.wordsPerMinute}}
                </td>
                <td data-test-review-comments class="bg-juicy-orange-50 truncate p-3 text-left">
                  {{formatValue row.studentReviewForAssignment.comments}}
                </td>
                <td data-test-review-button class="bg-juicy-orange-50 p-3 text-left whitespace-nowrap">
                  {{#if row.studentReviewForAssignment}}
                    <UiButton {{on "click" (fn this.editReview row.studentReviewForAssignment)}}>
                      {{t "assignments.editReview"}}
                    </UiButton>
                  {{else if (or (eq row.status "completed") (eq row.status "in-progress"))}}
                    {{#if row.student}}
                      <div class="w-fit {{if (eq row.status 'in-progress') 'cursor-not-allowed'}}">
                        <UiButton
                          @appearance={{ButtonAppearance.regular}}
                          @disabled={{eq row.status "in-progress"}}
                          {{on "click" (fn this.createReview row.student)}}
                        >
                          {{t "assignments.review"}}

                        </UiButton>
                        {{#if (eq row.status "in-progress")}}
                          <Tooltip @text={{t "assignments.disabledReviewTooltip"}} />
                        {{/if}}
                      </div>
                    {{/if}}
                  {{/if}}
                </td>
              </tr>
            {{else}}
              <tr>
                <td class="bg-juicy-orange-50 p-3" {{colspanMax}}>
                  <div class="missing-students-data">
                    {{t "assignments.missingStudentsDataInList"}}
                  </div>
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      {{/let}}
    </div>

    {{#if this.editReviewRecord}}
      <ReadAloudReviewModal @assignment={{@assignment}} @review={{this.editReviewRecord}} @close={{this.closeReview}} />
    {{/if}}
  </template>
}
